/**
 * The attribute track (issue #9): what each attribute modifier is before and after every boost
 * in the log, following Foundry's `prepareBuildData` arithmetic (a boost adds 1, or a half when
 * the modifier is already +4 or more: a partial boost). Ancestry, background and class boosts
 * come from the engine's build data (their Entries resolve the fixed slots); the free boosts at
 * each Level come from the Character's `attribute-boosts` Choices in Level and ordinal order.
 */
import type { Character, LevelNumber } from "../character/model";
import type { ActorData } from "../engine/actor-data";
import { ATTRIBUTES, isAttribute, type Attribute } from "../engine/values";

export type Mods = Record<Attribute, number>;

export interface LevelBoost {
  level: LevelNumber;
  ordinal: number;
  attribute: Attribute;
}

export interface AttributeTrack {
  /** Modifiers after the ancestry, background and class boosts, before any Level's free boosts. Fractions kept. */
  base: Mods;
  /** Every free boost in the log, in the order it applies. */
  boosts: LevelBoost[];
  /** Fractional modifiers before the boost at a Level and ordinal (the boosts of earlier Levels and earlier ordinals applied). */
  before(level: LevelNumber, ordinal: number): Mods;
  /** Whole modifiers after every boost at Levels up to and including `level` (what the engine derives at that Level). */
  modAt(level: LevelNumber): Mods;
  /** Whether a boost to the attribute at that point is partial (the modifier is already +4 or more). */
  isPartial(level: LevelNumber, ordinal: number, attribute: Attribute): boolean;
}

function zero(): Mods {
  return { str: 0, dex: 0, con: 0, int: 0, wis: 0, cha: 0 };
}

function boost(mods: Mods, attribute: Attribute): void {
  mods[attribute] += mods[attribute] >= 4 ? 0.5 : 1;
}

/** The free boosts in the log (Levels above the Character's Level included, for planning ahead), in order. */
export function levelBoosts(character: Character): LevelBoost[] {
  const out: LevelBoost[] = [];
  for (const level of character.levels) {
    const at = level.choices
      .filter((c) => c.source.kind === "progression" && c.source.slot === "attribute-boosts")
      .map((c) => ({ ordinal: (c.source as { ordinal: number }).ordinal, answers: c.answers ?? {} }))
      .sort((a, b) => a.ordinal - b.ordinal);
    for (const { ordinal, answers } of at) {
      for (const [k, v] of Object.entries(answers)) if (/^boost(:\d+)?$/.test(k) && isAttribute(v)) out.push({ level: level.number, ordinal, attribute: v });
    }
  }
  return out;
}

export function attributeTrack(character: Character, build: ActorData["system"]["build"]["attributes"]): AttributeTrack {
  const base = zero();
  for (const a of build.boosts.ancestry) boost(base, a);
  for (const a of build.flaws.ancestry) base[a] -= 1;
  for (const a of build.boosts.background) boost(base, a);
  if (build.boosts.class) boost(base, build.boosts.class);
  const boosts = levelBoosts(character);
  const upTo = (stop: (b: LevelBoost) => boolean): Mods => {
    const mods = { ...base };
    for (const b of boosts) {
      if (stop(b)) break;
      boost(mods, b.attribute);
    }
    return mods;
  };
  const whole = (mods: Mods): Mods => {
    const out = zero();
    for (const a of ATTRIBUTES) out[a] = Math.trunc(Math.min(Math.max(mods[a], -5), 10));
    return out;
  };
  return {
    base,
    boosts,
    before: (level, ordinal) => upTo((b) => b.level > level || (b.level === level && b.ordinal >= ordinal)),
    modAt: (level) => whole(upTo((b) => b.level > level)),
    isPartial(level, ordinal, attribute) {
      return this.before(level, ordinal)[attribute] >= 4;
    },
  };
}
